import { FeatureTypes } from "entity-app/constants";
import { ELinkType, TFeatureName, TLinkSource } from "../models";

type TLinkApi = {
  source_instance_id: string;
  target_instance_id: string;
  link_type?: ELinkType;
  reverse_dependency?: boolean;
  offset_duration?: number | null;
};

/**
 * ### Material -> Submittal and Submittal -> Material
 * **Note:** Always Submittal will be SOURCE and Material will be TARGET.
 * - **Add:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Submittal Id",
 *     "link_type": 0
 *   }
 */
const insertSubmittalMaterialObject = ({
  submittal_id,
  material_id
}: {
  submittal_id: string;
  material_id: string;
}): TLinkApi => {
  return {
    target_instance_id: material_id,
    source_instance_id: submittal_id,
    link_type: 0
  };
};

/**
 * ### Material -> Submittal and Submittal -> Material
 * **Note:** Always Submittal will be SOURCE and Material will be TARGET.
 * - **Remove:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Submittal Id"
 *   }
 *   ```
 */
const removeSubmittalMaterialObject = ({
  submittal_id,
  material_id
}: {
  submittal_id: string;
  material_id: string;
}): TLinkApi => {
  return {
    target_instance_id: material_id,
    source_instance_id: submittal_id
  };
};

/**
 *  * ### Submittal -> Activity
 * **Note:** Always Submittal will be SOURCE and Activity will be TARGET.
 * - **Add:**
 *   ```json
 *   {
 *     "target_instance_id": "Activity Id",
 *     "source_instance_id": "Submittal Id",
 *     "link_type": 0 // or 2, based on start-date (0) and end-date (2)
 *   }
 */
const insertSubmittalActivityObject = ({
  submittal_id,
  activity_id,
  link_type
}: {
  submittal_id: string;
  activity_id: string;
  link_type: ELinkType;
}): TLinkApi => {
  return {
    target_instance_id: activity_id,
    source_instance_id: submittal_id,
    link_type
  };
};

/**
 * ### Submittal -> Activity
 * **Note:** Always Submittal will be SOURCE and Activity will be TARGET.
 * - **Remove:**
 *   ```json
 *   {
 *     "target_instance_id": "Activity Id",
 *     "source_instance_id": "Submittal Id"
 *   }
 * @returns
 */
const removeSubmittalActivityObject = ({
  submittal_id,
  activity_id
}: {
  submittal_id: string;
  activity_id: string;
}): TLinkApi => {
  return {
    target_instance_id: activity_id,
    source_instance_id: submittal_id
  };
};

/**
 * #### Case 1: If Reverse Dependency is false (Material is SOURCE, Activity is TARGET)
 * - **Add:**
 *   ```json
 *   {
 *     "target_instance_id": "Activity Id",
 *     "source_instance_id": "Material Id",
 *     "link_type": 0, // or 2, based on start-date (0) and end-date (2)
 *     "reverse_dependency": false,
 *     "offset_duration": number | null
 *   }
 *  #### Case 2: If Reverse Dependency is true (Activity is SOURCE, Material is TARGET)
 * - **Add:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Activity Id",
 *     "link_type": 0, // or 1, based on start-date (1) and end-date (0)
 *     "reverse_dependency": true,
 *     "offset_duration": null
 *   }
 */
const insertMaterialActivityObject = ({
  activity_id,
  material_id,
  link_type,
  reverse_dependency,
  offset_duration
}: {
  activity_id: string;
  material_id: string;
  link_type: ELinkType;
  reverse_dependency: boolean;
  offset_duration: number | null;
}): TLinkApi => {
  return reverse_dependency
    ? {
        target_instance_id: material_id,
        source_instance_id: activity_id,
        link_type,
        reverse_dependency: true,
        offset_duration: null // if reverse TRUE then it should be null only
      }
    : {
        target_instance_id: activity_id,
        source_instance_id: material_id,
        link_type,
        offset_duration
      };
};

/**
 * #### Case 1: If Reverse Dependency is false (Material is SOURCE, Activity is TARGET)
 * - **Remove:**
 *   ```json
 *   {
 *     "target_instance_id": "Activity Id",
 *     "source_instance_id": "Material Id"
 *   }
 *  #### Case 2: If Reverse Dependency is true (Activity is SOURCE, Material is TARGET)
 * - **Remove:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Activity Id"
 *   }
 *   ```
 */
const removeMaterialActivityObject = ({
  activity_id,
  material_id,
  reverse_dependency
}: {
  activity_id: string;
  material_id: string;
  reverse_dependency: boolean;
}): TLinkApi => {
  return reverse_dependency
    ? {
        target_instance_id: material_id,
        source_instance_id: activity_id
      }
    : {
        target_instance_id: activity_id,
        source_instance_id: material_id
      };
};

/**
 * ### Material -> Material
 * **Note:** Material is SOURCE and Material is TARGET.
 * - **Add:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Material Id",
 *     "link_type": 0
 *   }
 */
const insertMaterialMaterialObject = ({
  source_material_id,
  target_material_id
}: {
  source_material_id: string;
  target_material_id: string;
}): TLinkApi => {
  return {
    target_instance_id: target_material_id,
    source_instance_id: source_material_id,
    link_type: 0
  };
};

/**
 * ### Material -> Material
 * **Note:** Material is SOURCE and Material is TARGET.
 * - **Remove:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Material Id",
 *     "link_type": 0
 *   }
 */
const removeMaterialMaterialObject = ({
  source_material_id,
  target_material_id
}: {
  source_material_id: string;
  target_material_id: string;
}): TLinkApi => {
  return {
    target_instance_id: target_material_id,
    source_instance_id: source_material_id
  };
};

/**
 * Generates the payload for linking entities by determining which links need to be inserted, updated, or deleted.
 *
 * Compares the current list of linked entities (`editingEntity.list`) with the previous list (`blockListData`)
 * for a specific feature, and produces arrays of insert, update, and delete operations to synchronize the state.
 *
 * @param params - The parameters for generating the linking payload.
 * @param params.featureDetail - The detail object for the current feature.
 * @param params.selectedEntityInfo - The entity currently selected for linking.
 * @param params.blockListData - The list of entities and their current links before editing.
 * @param params.editingEntity - The entity and its edited list of links.
 * @returns An object containing arrays of inserts, updates, and deletes to be applied.
 */
/**
 * Generates the payload for linking, updating, or removing entity relationships between features (such as Material, Submittal, and Activity).
 *
 * https://constructiviq.atlassian.net/browse/CIQ-7208?focusedCommentId=26442
 * @param params - An object containing:
 *   - featureDetail: The detail of the current feature.
 *   - selectedEntityInfo: The selected entity to link.
 *   - blockListData: The current list of entities before editing.
 *   - editingEntity: The entity list after editing.
 * @returns An object containing arrays of inserts, updates, and deletes for the linking API.
 *
 * ---
 * ## Linking Logic
 *
 * ### Material -> Submittal and Submittal -> Material
 * **Note:** Always Submittal will be SOURCE and Material will be TARGET.
 * - **Add:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Submittal Id",
 *     "link_type": 0
 *   }
 *   ```
 * - **Remove:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Submittal Id"
 *   }
 *   ```
 * - **Update:** Not required for now.
 *
 * ---
 * ### Submittal -> Activity
 * **Note:** Always Submittal will be SOURCE and Activity will be TARGET.
 * - **Add:**
 *   ```json
 *   {
 *     "target_instance_id": "Activity Id",
 *     "source_instance_id": "Submittal Id",
 *     "link_type": 0 // or 2, based on start-date (0) and end-date (2)
 *   }
 *   ```
 * - **Remove:**
 *   ```json
 *   {
 *     "target_instance_id": "Activity Id",
 *     "source_instance_id": "Submittal Id"
 *   }
 *   ```
 * - **Update:** Not required for now.
 *
 * ---
 * ### Material -> Activity
 * #### Case 1: If Reverse Dependency is false (Material is SOURCE, Activity is TARGET)
 * - **Add:**
 *   ```json
 *   {
 *     "target_instance_id": "Activity Id",
 *     "source_instance_id": "Material Id",
 *     "link_type": 0, // or 2, based on start-date (0) and end-date (2)
 *     "reverse_dependency": false,
 *     "offset_duration": number | null
 *   }
 *   ```
 * - **Remove:**
 *   ```json
 *   {
 *     "target_instance_id": "Activity Id",
 *     "source_instance_id": "Material Id"
 *   }
 *   ```
 * - **Update:**
 *   - Update `offset_duration` (insert again with null or new value):
 *     ```json
 *     {
 *       "target_instance_id": "Activity Id",
 *       "source_instance_id": "Material Id",
 *       "link_type": 0, // or 2
 *       "reverse_dependency": false,
 *       "offset_duration": number | null
 *     }
 *     ```
 *   - Update Reverse Dependency (was false, change to true):
 *     - Remove:
 *       ```json
 *       {
 *         "target_instance_id": "Activity Id",
 *         "source_instance_id": "Material Id"
 *       }
 *       ```
 *     - Add:
 *       ```json
 *       {
 *         "target_instance_id": "Material Id",
 *         "source_instance_id": "Activity Id",
 *         "link_type": 0, // or 1, based on start-date (1) and end-date (0)
 *         "reverse_dependency": true,
 *         "offset_duration": null
 *       }
 *       ```
 *
 * #### Case 2: If Reverse Dependency is true (Activity is SOURCE, Material is TARGET)
 * - **Add:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Activity Id",
 *     "link_type": 0, // or 1, based on start-date (1) and end-date (0)
 *     "reverse_dependency": true,
 *     "offset_duration": null
 *   }
 *   ```
 * - **Remove:**
 *   ```json
 *   {
 *     "target_instance_id": "Material Id",
 *     "source_instance_id": "Activity Id"
 *   }
 *   ```
 * - **Update:**
 *   - Update `offset_duration` (insert again with null or new value):
 *     ```json
 *     {
 *       "target_instance_id": "Material Id",
 *       "source_instance_id": "Activity Id",
 *       "link_type": 0, // or 1
 *       "reverse_dependency": true,
 *       "offset_duration": null
 *     }
 *     ```
 *   - Update Reverse Dependency (was true, change to false):
 *     - Remove:
 *       ```json
 *       {
 *         "target_instance_id": "Material Id",
 *         "source_instance_id": "Activity Id"
 *       }
 *       ```
 *     - Add:
 *       ```json
 *       {
 *         "target_instance_id": "Activity Id",
 *         "source_instance_id": "Material Id",
 *         "link_type": 0, // or 2, based on start-date (0) and end-date (2)
 *         "reverse_dependency": false,
 *         "offset_duration": number | null
 *       }
 *       ```
 */
export const generateLinkingPayload = ({
  selectedEntityInfo,
  newEntityLinks,
  oldEntityLinks,
  cardsFeatureName
}: {
  selectedEntityInfo: {
    parentFeatureDetail: TFeatureName;
    ids: string[];
    predecessors?: any[];
  };
  cardsFeatureName: TFeatureName;
  oldEntityLinks: TLinkSource[];
  newEntityLinks: TLinkSource[];
}) => {
  const inserts: TLinkApi[] = [];
  const deletes: TLinkApi[] = [];
  const offMaterialTrackingOfSubmittalIds: string[] = [];

  const { parentFeatureDetail } = selectedEntityInfo;

  // -----------------------------
  // Helpers
  // -----------------------------
  const addInsert = (ent: TLinkSource) => {
    if (
      parentFeatureDetail === "MATERIALS" &&
      cardsFeatureName === "SUBMITTALS"
    ) {
      const materialTracking =
        ent.feature_instance?.feature_submittals?.[0]?.material_tracking;
      if (!materialTracking) {
        offMaterialTrackingOfSubmittalIds.push(ent.featureInstanceId);
      }
      selectedEntityInfo.ids.forEach((id) => {
        inserts.push(
          insertSubmittalMaterialObject({
            material_id: id,
            submittal_id: ent.featureInstanceId
          })
        );
      });
    }

    if (
      parentFeatureDetail === "SUBMITTALS" &&
      cardsFeatureName === "MATERIALS"
    ) {
      selectedEntityInfo.ids.forEach((id) => {
        inserts.push(
          insertSubmittalMaterialObject({
            material_id: ent.featureInstanceId,
            submittal_id: id
          })
        );
      });
    }

    if (
      parentFeatureDetail === "SUBMITTALS" &&
      cardsFeatureName === "SCHEDULES"
    ) {
      selectedEntityInfo.ids.forEach((id) => {
        inserts.push(
          insertSubmittalActivityObject({
            activity_id: ent.featureInstanceId,
            link_type: ent.link_type,
            submittal_id: id
          })
        );
      });
    }

    if (
      parentFeatureDetail === "MATERIALS" &&
      cardsFeatureName === "SCHEDULES"
    ) {
      selectedEntityInfo.ids.forEach((id) => {
        inserts.push(
          insertMaterialActivityObject({
            activity_id: ent.featureInstanceId,
            link_type: ent.link_type,
            offset_duration: ent.offset_duration,
            reverse_dependency: ent.reverse_dependency,
            material_id: id
          })
        );
      });
    }

    if (
      parentFeatureDetail === "MATERIALS" &&
      cardsFeatureName === "MATERIALS"
    ) {
      selectedEntityInfo.ids.forEach((id) => {
        inserts.push(
          insertMaterialMaterialObject({
            source_material_id: id,
            target_material_id: ent.featureInstanceId
          })
        );
      });
    }
  };

  const addDelete = (oldEnt: TLinkSource) => {
    if (
      parentFeatureDetail === "MATERIALS" &&
      cardsFeatureName === "SUBMITTALS"
    ) {
      selectedEntityInfo.ids.forEach((id) => {
        deletes.push(
          removeSubmittalMaterialObject({
            material_id: id,
            submittal_id: oldEnt.featureInstanceId
          })
        );
      });
    }

    if (
      parentFeatureDetail === "SUBMITTALS" &&
      cardsFeatureName === "MATERIALS"
    ) {
      selectedEntityInfo.ids.forEach((id) => {
        deletes.push(
          removeSubmittalMaterialObject({
            material_id: oldEnt.featureInstanceId,
            submittal_id: id
          })
        );
      });
    }

    if (
      parentFeatureDetail === "SUBMITTALS" &&
      cardsFeatureName === "SCHEDULES"
    ) {
      selectedEntityInfo.ids.forEach((id) => {
        deletes.push(
          removeSubmittalActivityObject({
            activity_id: oldEnt.featureInstanceId,
            submittal_id: id
          })
        );
      });
    }

    if (
      parentFeatureDetail === "MATERIALS" &&
      cardsFeatureName === "SCHEDULES"
    ) {
      selectedEntityInfo.ids.forEach((id) => {
        deletes.push(
          removeMaterialActivityObject({
            activity_id: oldEnt.featureInstanceId,
            reverse_dependency: oldEnt.reverse_dependency,
            material_id: id
          })
        );
      });
    }

    if (
      parentFeatureDetail === "MATERIALS" &&
      cardsFeatureName === "MATERIALS"
    ) {
      const predecessorsArray = (selectedEntityInfo.predecessors || []).map(
        (pred) => pred.source_feature_instance.id
      );

      selectedEntityInfo.ids.forEach((id) => {
        if (predecessorsArray.includes(oldEnt.featureInstanceId)) {
          deletes.push(
            removeMaterialMaterialObject({
              source_material_id: oldEnt.featureInstanceId,
              target_material_id: id
            })
          );
        } else
          deletes.push(
            removeMaterialMaterialObject({
              source_material_id: id,
              target_material_id: oldEnt.featureInstanceId
            })
          );
      });
    }
  };

  // -----------------------------
  // Handle deletes + updates
  // -----------------------------
  oldEntityLinks?.forEach((oldEnt) => {
    const match = newEntityLinks?.find(
      (x) => x?.featureInstanceId === oldEnt?.featureInstanceId
    );

    if (!match) {
      // Missing → delete
      addDelete(oldEnt);
      return;
    }

    // Exists → check if changed
    const changed =
      oldEnt.link_type !== match.link_type ||
      oldEnt.reverse_dependency !== match.reverse_dependency ||
      oldEnt.offset_duration !== match.offset_duration;

    if (changed) {
      if (oldEnt.reverse_dependency !== match.reverse_dependency) {
        // Reverse link changed → delete old
        addDelete(oldEnt);
      }
      // Insert new as update
      addInsert(match);
    }

    // Recursive case: Material → Submittal → Activities
    if (
      cardsFeatureName === "MATERIALS" &&
      oldEnt.feature_instance?.links &&
      match.feature_instance?.links
    ) {
      const nested = generateLinkingPayload({
        oldEntityLinks: oldEnt.feature_instance.links,
        newEntityLinks: match.feature_instance.links,
        cardsFeatureName: "SCHEDULES",
        selectedEntityInfo: {
          ids: [oldEnt.featureInstanceId],
          parentFeatureDetail: "MATERIALS"
        }
      });
      inserts.push(...nested.inserts);
      deletes.push(...nested.deletes);
    }
  });

  // -----------------------------
  // Handle inserts (new only)
  // -----------------------------
  newEntityLinks?.forEach((ent) => {
    const exists = oldEntityLinks?.find(
      (x) => x?.featureInstanceId === ent?.featureInstanceId
    );

    if (!exists) {
      addInsert(ent);

      // On Submittal Linking page: New Material linked and then linked activity of Material
      if (
        ent.feature_instance?.feature.id === FeatureTypes.MATERIALS &&
        ent.feature_instance.meta?.originalLinks
      ) {
        const nested = generateLinkingPayload({
          oldEntityLinks: ent.feature_instance.meta.originalLinks || [],
          newEntityLinks: ent.feature_instance.links,
          cardsFeatureName: "SCHEDULES",
          selectedEntityInfo: {
            ids: [ent.featureInstanceId],
            parentFeatureDetail: "MATERIALS"
          }
        });
        inserts.push(...nested.inserts);
        deletes.push(...nested.deletes);
      }
    }
  });

  return {
    inserts,
    deletes,
    offMaterialTrackingOfSubmittalIds
  };
};
